import { Link } from "react-router-dom";
import { Star, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MailboxNavBadge } from "@/components/mailbox/useMailboxBadge";

export const NavLink = ({ item, mobile = false, ns = "", location, favorites, unreadCount, mailboxBadge, onToggleFavorite, onNavigate }) => {
  const isActive = item.match
    ? item.match(location.pathname)
    : location.pathname === item.path ||
      (item.path !== "/" && location.pathname.startsWith(item.path));
  const Icon = item.icon;
  const isFav = favorites.includes(item.path);
  const hasBadge = (item.path === "/chat" && unreadCount > 0) || (item.path === "/mailbox" && mailboxBadge?.pending > 0);
  // Mit Zähler: Stern erst beim Überfahren, Zähler macht dann Platz – so bleibt der Name lesbar
  const badgeSwap = hasBadge && !isFav && !mobile;
  const testKey = `${ns}${item.path.replace("/", "") || "dashboard"}`;

  return (
    <div className="group relative flex items-center">
      <Link
        to={item.path}
        onClick={() => mobile && onNavigate?.()}
        className={`
          flex-1 flex items-center ${badgeSwap ? "gap-2.5 pr-2" : "gap-3 pr-11"} pl-4 py-3 rounded-lg font-medium min-w-0
          transition-colors duration-150
          ${isActive
            ? "bg-primary text-primary-foreground"
            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          }
        `}
        data-testid={`nav-${testKey}`}
      >
        <Icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
        <span className="truncate" title={item.label}>{item.label}</span>
        {item.path === "/chat" && unreadCount > 0 && (
          <span
            className={`ml-auto ${badgeSwap ? "transition-opacity group-hover:opacity-0" : "mr-5"} shrink-0 min-w-[20px] h-5 px-1.5 flex items-center justify-center text-[11px] font-bold rounded-full bg-red-500 text-white`}
            data-testid="nav-chat-unread-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
        {item.path === "/mailbox" && <MailboxNavBadge badge={mailboxBadge} swap={badgeSwap} />}
      </Link>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite(item.path); }}
        className={`
          absolute right-2 p-1.5 rounded-md transition-opacity
          ${isFav || mobile ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"}
          ${isActive ? "text-primary-foreground/80 hover:text-primary-foreground" : "text-muted-foreground/60 hover:text-foreground"}
        `}
        data-testid={`nav-fav-toggle-${testKey}`}
        aria-label={isFav ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
        title={isFav ? "Aus Favoriten entfernen" : "Anheften"}
      >
        <Star className="w-4 h-4" fill={isFav ? "currentColor" : "none"} strokeWidth={1.5} />
      </button>
    </div>
  );
};

const SortableFavItem = ({ item, mobile = false, navLinkProps }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.path });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };
  const key = item.path.replace("/", "") || "dashboard";
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1 rounded-lg ${isDragging ? "opacity-80 bg-accent/60 shadow-sm" : ""}`}
      data-testid={`nav-fav-sortable-${key}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        style={{ touchAction: "none" }}
        className="p-1.5 rounded-md text-muted-foreground/50 hover:text-foreground cursor-grab active:cursor-grabbing shrink-0"
        data-testid={`nav-fav-drag-${key}`}
        aria-label={`${item.label} verschieben`}
        title="Zum Sortieren ziehen"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-0">
        <NavLink item={item} mobile={mobile} ns="fav-" {...navLinkProps} />
      </div>
    </div>
  );
};

export const FavoritesSection = ({ mobile = false, favorites, favoriteItems, onDragEnd, navLinkProps }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (favoriteItems.length === 0) return null;
  return (
    <div className="pb-4 mb-2 border-b border-border" data-testid="nav-section-favorites">
      <div className="flex items-center gap-2 px-4 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
        <Star className="w-3.5 h-3.5" fill="currentColor" />
        <span>Favoriten</span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={favorites} strategy={verticalListSortingStrategy}>
          <div className="space-y-2" data-testid="nav-section-favorites-items">
            {favoriteItems.map((item) => (
              <SortableFavItem key={`fav-${item.path}`} item={item} mobile={mobile} navLinkProps={navLinkProps} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
